import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  UserCog, 
  FileText, 
  Calendar, 
  Receipt, 
  CreditCard, 
  BarChart3, 
  Settings,
  CheckSquare,
  FolderOpen,
  LogOut,
  FileInput,
  Layers,
  Book,
  Wallet,
  DollarSign,
  ClipboardList,
  MessageSquare,
  Send
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Work Orders', href: '/work-orders', icon: FileText },
  { name: 'Calendar', href: '/calendar', icon: Calendar },
  { name: 'Communications', href: '/communications', icon: MessageSquare },
  { name: 'Email & SMS Blasts', href: '/blasts', icon: Send },
  { name: 'Clients', href: '/clients', icon: Users },
  { name: 'Vendors', href: '/vendors', icon: UserCog },
  { name: 'Tasks', href: '/tasks', icon: CheckSquare },
  { name: 'Files', href: '/files', icon: FolderOpen },
  { name: 'Encounters', href: '/encounters', icon: ClipboardList },
  { name: 'Invoices', href: '/invoices', icon: Receipt },
  { name: 'Consolidated', href: '/consolidated-invoicing', icon: Layers },
  { name: 'Vendor Invoices', href: '/vendor-invoices', icon: FileInput },
  { name: 'Payables', href: '/payables', icon: CreditCard },
  { name: 'Vendor Payroll', href: '/vendor-payroll', icon: Wallet },
  { name: 'Payments', href: '/payments', icon: DollarSign },
  { name: 'Reports', href: '/reports-dashboard', icon: BarChart3 },
  { name: 'Knowledge Base', href: '/knowledge-base', icon: Book },
  { name: 'Settings', href: '/settings', icon: Settings },
];

export const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();

  return (
    <div className="flex flex-col h-full w-64 bg-gradient-to-b from-blue-600 to-blue-700 text-white">
      {/* Logo - White background */}
      <div className="flex items-center justify-center h-20 px-4 bg-white">
        <img 
          src="https://customer-assets.emergentagent.com/job_457f63c4-6a2a-4995-8167-0b04779a6e2e/artifacts/c21k9zo8_bylyngo%20interpreting.png" 
          alt="Bylyngo"
          className="h-14 w-auto"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navigation.map((item) => {
          const isActive = location.pathname === item.href || 
            (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
          
          return (
            <NavLink
              key={item.name}
              to={item.href}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                isActive
                  ? 'bg-white text-blue-600 shadow-md'
                  : 'text-blue-100 hover:bg-blue-500/50 hover:text-white'
              )}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </NavLink>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-blue-500/30">
        <div className="flex items-center gap-3 px-3 py-2">
          {user?.picture ? (
            <img 
              src={user.picture} 
              alt={user.name} 
              className="w-10 h-10 rounded-full object-cover border-2 border-white/30"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30">
              <span className="text-white font-semibold">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-blue-200 truncate capitalize">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="flex items-center gap-3 w-full px-3 py-2.5 mt-2 rounded-lg text-sm font-medium text-blue-100 hover:bg-red-500/80 hover:text-white transition-all duration-200"
        >
          <LogOut className="h-5 w-5" />
          Sign Out
        </button>
      </div>
    </div>
  );
};
